import React, { useState } from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import { dict, SUPPORTED_LANGS } from "../i18n";
import { pagePath, translatePath } from "../lib/routes";
import { PRICING_SECTIONS } from "../lib/pricingSections";
import { DISEASE_SLUGS } from "../data/diseaseSlugs";

// Sub-slug translator for translatePath: given a parent page key and an
// (any-language) sub-slug, return the equivalent sub-slug in targetLang.
function subSlugTranslator(pageKey, subSlug, targetLang) {
  if (pageKey === "pricing") {
    const section = PRICING_SECTIONS.find(
      (s) => s.slugs.et === subSlug || s.slugs.ru === subSlug || s.slugs.en === subSlug,
    );
    return section ? section.slugs[targetLang] : subSlug;
  }
  if (pageKey === "diseases") {
    const article = DISEASE_SLUGS.find(
      (d) => d.slugs.et === subSlug || d.slugs.ru === subSlug || d.slugs.en === subSlug,
    );
    return article ? article.slugs[targetLang] : subSlug;
  }
  return subSlug;
}

export default function Header({ lang }) {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const t = dict[lang].nav;

  const links = [
    { to: pagePath("home", lang), label: t.home, end: true },
    { to: pagePath("services", lang), label: t.services },
    { to: pagePath("diseases", lang), label: t.diseases },
    { to: pagePath("doctors", lang), label: t.doctors },
    { to: pagePath("contact", lang), label: t.contact },
  ];

  const switchLang = (newLang) => {
    // Translate pathname, but preserve any query string and hash so pricing
    // anchors and legacy ?show= filters survive language switches.
    const target = translatePath(loc.pathname, newLang, subSlugTranslator) + loc.search + loc.hash;
    nav(target);
    setLangOpen(false);
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full border-b border-surface-outline/40 bg-white/85 backdrop-blur-md"
    >
      <div className="container-page flex h-16 items-center justify-between">
        <Link
          to={pagePath("home", lang)}
          data-testid="header-logo"
          className="flex items-center gap-2 group"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sapphire-700 text-white font-semibold tracking-tight">
            TE
          </span>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold text-surface-ink">Tallinna</div>
            <div className="text-[12px] text-surface-inkSoft -mt-0.5">Endokrinoloogia Kliinik</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-lg text-[14px] font-medium transition-colors ${
                  isActive
                    ? "bg-sapphire-50 text-sapphire-700"
                    : "text-surface-inkSoft hover:text-sapphire-700 hover:bg-sapphire-50/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+37255665451"
            data-testid="header-phone"
            className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium text-surface-ink hover:text-sapphire-700 transition-colors"
          >
            <Phone className="h-4 w-4" />
            +372 5566 5451
          </a>

          <div className="relative">
            <button
              onClick={() => setLangOpen((o) => !o)}
              data-testid="lang-switcher-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold uppercase text-surface-ink hover:bg-sapphire-50 transition-colors"
              aria-label="Language"
            >
              <Globe className="h-4 w-4" />
              {lang}
            </button>
            {langOpen && (
              <div
                className="absolute right-0 mt-2 w-32 rounded-xl border border-surface-outline/60 bg-white shadow-ambientLg p-1 z-50"
                data-testid="lang-dropdown"
              >
                {SUPPORTED_LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => switchLang(l)}
                    data-testid={`lang-option-${l}`}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                      l === lang ? "bg-sapphire-50 text-sapphire-700" : "text-surface-ink hover:bg-sapphire-50/60"
                    }`}
                  >
                    {l === "et" ? "Eesti" : l === "ru" ? "Русский" : "English"}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-btn"
            className="hidden md:inline-flex btn-primary !py-2 !min-h-0 text-[13px]"
          >
            {t.book}
          </a>

          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 rounded-lg hover:bg-sapphire-50"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-surface-outline/40 bg-white" data-testid="mobile-menu">
          <div className="container-page py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-[15px] font-medium ${
                    isActive ? "bg-sapphire-50 text-sapphire-700" : "text-surface-ink hover:bg-sapphire-50/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="tel:+37255665451"
              className="px-4 py-3 rounded-lg text-[15px] font-medium text-surface-inkSoft flex items-center gap-2"
            >
              <Phone className="h-4 w-4" />
              +372 5566 5451
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
